import { IResponseDataBridgeClient } from './IResponseDataBridgeClient';
import SignalsService from '../services/core/SignalsService';
import type { ITelemetryBeaconService } from '../services/core/TelemetryBeaconService';
import {
    PackResponseInputMessage,
    InitResponseInputMessage,
    SetDataInputMessage,
    GetDataInputMessage,
    HasExistingDataInputMessage,
    HasExistingDataResultOutputMessage,
    ResponseDataBridgeOutputMessageType,
    InitResponseCompletedOutputMessage,
    PackResponseCompleteOutputMessage,
    PackResponseErrorOutputMessage,
    BackupResponseDataInputMessage,
    BackupResponseDataCompleteOutputMessage,
    BackupResponseDataFailedOutputMessage
} from '../../../../serviceworker/src/bridges/responseDataBridgeMessages';

const swc = ((navigator as Navigator).serviceWorker as ServiceWorkerContainer);

/** Messages handled by PWAResponseSupportBridgeClient — skip them in the data bridge client. */
const SUPPORT_BRIDGE_TYPES: ReadonlySet<string> = new Set([
    ResponseDataBridgeOutputMessageType.listAllResponsesResult,
    ResponseDataBridgeOutputMessageType.getResponseDataJsonResult,
    ResponseDataBridgeOutputMessageType.listPendingRecoveriesResult,
    ResponseDataBridgeOutputMessageType.pendingRecoveryRetrySuccess,
    ResponseDataBridgeOutputMessageType.pendingRecoveryRetryFailed,
    ResponseDataBridgeOutputMessageType.pendingRecoveryDeleted,
    ResponseDataBridgeOutputMessageType.pendingRecoveryCountResult,
    ResponseDataBridgeOutputMessageType.retryAllPendingRecoveriesComplete,
]);

export default class PWAResponseDataBridgeClient implements IResponseDataBridgeClient {
    private signalsService: SignalsService;    
    private telemetryBeaconService: ITelemetryBeaconService;
    private instanceId: string;
    private dataStore: Map<string, string|null>;
    private pendingSetDataPromises: Map<string, { resolve: Function, reject: Function }>;
    private pendingWritePromises: Set<Promise<void>>;
    private pendingResponseCount: Map<string, number>;

    constructor(signalsService: SignalsService, telemetryBeaconService: ITelemetryBeaconService) {
        this.instanceId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        this.signalsService = signalsService;
        this.telemetryBeaconService = telemetryBeaconService;
        this.dataStore = new Map();
        this.pendingSetDataPromises = new Map();
        this.pendingWritePromises = new Set();
        this.pendingResponseCount = new Map();
        this.initHandlers();
    }

    private initHandlers() {
        swc.addEventListener('message', (event) => {
            const message = event.data;

            if (!Object.values(ResponseDataBridgeOutputMessageType).includes(message.type)) {
                return; // Not our message
            }
            if (SUPPORT_BRIDGE_TYPES.has(message.type)) {
                return;
            }
            if (message.type !== ResponseDataBridgeOutputMessageType.setDataComplete) { // setDataComplete is spammy
                console.log("PWAResponseDataBridgeClient Received message from service worker", message, this.instanceId);
            }
            switch (message.type) {
                case ResponseDataBridgeOutputMessageType.packResponseComplete:
                    this.signalsService.emit('afterPacked', message);
                    break;
                case ResponseDataBridgeOutputMessageType.packResponseError:
                    const packResponseErrorMessage = message as PackResponseErrorOutputMessage;
                    // Sentry: capture with full context including the response JSON
                    // snapshot as an attachment — this is the primary data needed
                    // to debug serialization/packing failures.
                    if (typeof window !== 'undefined' && (window as any).Sentry) {
                        const Sentry = (window as any).Sentry;
                        const sentryError = new Error(`packResponse: ${packResponseErrorMessage.error}`);
                        if (packResponseErrorMessage.stack) {
                            sentryError.stack = packResponseErrorMessage.stack;
                        }
                        Sentry.withScope((scope: any) => {
                            scope.setTag('source', 'packResponse');
                            scope.setExtra('fileName', packResponseErrorMessage.fileName || 'unknown');
                            scope.setExtra('hasResponseDataJson', !!packResponseErrorMessage.responseDataJson);
                            if (packResponseErrorMessage.responseDataJson) {
                                if (typeof scope.addAttachment === 'function') {
                                    scope.addAttachment({
                                        filename: 'response-data.json',
                                        data: packResponseErrorMessage.responseDataJson,
                                        contentType: 'application/json',
                                    });
                                } else {
                                    // Fallback when addAttachment isn't available
                                    scope.setExtra('responseDataJson', packResponseErrorMessage.responseDataJson);
                                }
                            }
                            Sentry.captureException(sentryError);
                        });
                    }
                    // Telemetry: operational metric (separate from Sentry)
                    try {
                        this.telemetryBeaconService.record('pack.failed', 'critical', {
                            errorMessage: (packResponseErrorMessage.error || '').substring(0, 200),
                            fileName: packResponseErrorMessage.fileName || 'unknown',
                            hasResponseDataJson: String(!!packResponseErrorMessage.responseDataJson),
                        });
                    } catch (telemetryError) {
                        // Telemetry must never break pack error handling
                    }
                    this.signalsService.emit('afterPackedError', packResponseErrorMessage.error);
                    break;
                case ResponseDataBridgeOutputMessageType.hasExistingDataResult:
                    const hasExistingDataMessage = message as HasExistingDataResultOutputMessage;
                    this.signalsService.emit('hasExistingDataResult',
                        hasExistingDataMessage.hasExistingData,
                        hasExistingDataMessage.hasExistingDataOnDisk,
                        hasExistingDataMessage.hasExistingDataOnWebService,
                        hasExistingDataMessage.unixTimeStampUTC,
                        hasExistingDataMessage.candidateIdentity
                    );
                    break;
                case ResponseDataBridgeOutputMessageType.initResponseCompleted:
                    const initResponseCompletedMessage = message as InitResponseCompletedOutputMessage;
                    this.dataStore = initResponseCompletedMessage.data;
                    this.signalsService.emit('initResponseCompleted');
                    break;
                case ResponseDataBridgeOutputMessageType.setDataComplete:
                    this.settleSetDataResponse(message.key, p => p.resolve());
                    break;
                case ResponseDataBridgeOutputMessageType.setDataError:
                    this.settleSetDataResponse(message.key, p => p.reject(new Error(message.error)));
                    break;
                case ResponseDataBridgeOutputMessageType.backupResponseDataComplete:
                    const backupCompleteMessage = message as BackupResponseDataCompleteOutputMessage;
                    this.dataStore = new Map();
                    this.signalsService.emit('backupResponseDataComplete', backupCompleteMessage.backupDbName);
                    break;
                case ResponseDataBridgeOutputMessageType.backupResponseDataFailed:
                    const backupFailedMessage = message as BackupResponseDataFailedOutputMessage;
                    this.signalsService.emit('backupResponseDataFailed', backupFailedMessage.error);
                    break;
                default:
                    console.warn(`Unhandled message type: ${message.type}`);
                    break;
            }
        });
    }

    /**
     * Handles a SW response for a setData call. When multiple writes for the
     * same key are in flight, only the last response settles the active promise.
     */
    private settleSetDataResponse(key: string, settle: (p: { resolve: Function, reject: Function }) => void) {
        const remaining = (this.pendingResponseCount.get(key) || 1) - 1;
        if (remaining > 0) {
            this.pendingResponseCount.set(key, remaining);
            return;
        }
        this.pendingResponseCount.delete(key);
        const pending = this.pendingSetDataPromises.get(key);
        if (pending) {
            settle(pending);
            this.pendingSetDataPromises.delete(key);
        }
    }

    /**
     * Packs response data into a file.
     *
     * @param fileName - Name of the file to pack the response data into
     * @param recoveryMetadata - Optional metadata for creating a PendingRecoveryEntry in the SW
     */
    public packResponse(fileName: string, recoveryMetadata?: import('../../../../serviceworker/src/bridges/responseDataBridgeMessages').PackRecoveryMetadata): void {
        if (!swc.controller) {
            console.error("No service worker controller available");
            this.signalsService.emit('afterPackedError');
            return;
        }

        const message = new PackResponseInputMessage(fileName, recoveryMetadata);
        swc.controller.postMessage(message);
    }

    public async initResponse(useExistingData: boolean, useHybridResponse: boolean): Promise<void> {
        if (!swc.controller) {
            throw new Error("No service worker controller available");
        }

        swc.controller.postMessage(new InitResponseInputMessage(useExistingData, useHybridResponse));
        return Promise.resolve();
    }

    public async setData(key: string, value?: string): Promise<void> {
        this.dataStore.set(key, value || null);

        if (!swc.controller) {
            console.error("No service worker controller available");
            // Telemetry: SW controller lost during setData — critical for data persistence
            try {
                this.telemetryBeaconService.record('sw.controller_lost', 'critical', { key: key, context: 'setData' });
            } catch (telemetryError) {
                // Telemetry must never break setData
            }
            return;
        }

        // Resolve any previous pending promise for the same key — their value is being overwritten.
        const existing = this.pendingSetDataPromises.get(key);
        if (existing) {
            existing.resolve();
        }

        // Count outstanding SW responses so settleSetDataResponse skips superseded confirmations.
        const currentCount = this.pendingResponseCount.get(key) || 0;
        this.pendingResponseCount.set(key, currentCount + 1);

        const promise = new Promise<void>((resolve, reject) => {
            this.pendingSetDataPromises.set(key, { resolve, reject });
            swc.controller!.postMessage(new SetDataInputMessage(key, value || null));
        });

        this.pendingWritePromises.add(promise);
        promise.finally(() => {
            this.pendingWritePromises.delete(promise);
        });

        return promise;
    }

    public waitForPendingWrites(timeoutMs: number = 10000): Promise<void> {
        if (this.pendingWritePromises.size === 0) {
            return Promise.resolve();
        }

        console.log(`Waiting for ${this.pendingWritePromises.size} pending writes`);

        let timeoutId: ReturnType<typeof setTimeout>;
        const allWrites = Promise.allSettled(Array.from(this.pendingWritePromises))
            .then(() => { console.log('All pending writes settled'); });

        const timeout = new Promise<void>((resolve) => {
            timeoutId = setTimeout(() => {
                console.warn(`Timeout after ${timeoutMs}ms, ${this.pendingWritePromises.size} writes still pending`);
                resolve();
            }, timeoutMs);
        });

        return Promise.race([allWrites, timeout]).finally(() => {
            clearTimeout(timeoutId);
        });
    }

    public getData(key: string): string | null {

       

        const localValue = this.dataStore.get(key) || null;
        
        if (!swc.controller) {
            console.error("No service worker controller available");
            return localValue;
        }

        //Is this safe?!?! Won't it have race conditions against putData()? swc.controller.postMessage(new GetDataInputMessage(key));
        return localValue;
    }

    public backupAndClearResponseData(): void {
        if (!swc.controller) {
            console.error("No service worker controller available");
            this.signalsService.emit('backupResponseDataFailed', 'No service worker controller available');
            return;
        }

        swc.controller.postMessage(new BackupResponseDataInputMessage());
    }

    public hasExistingData(allowHybridResponse: boolean, hybridResponseURL: string | null, pirateToken: string): void {
        if (!swc.controller) {
            console.error("No service worker controller available");
            this.signalsService.emit('hasExistingDataResult', false, false, false, 0);
            return;
        }

        swc.controller.postMessage(new HasExistingDataInputMessage(
            allowHybridResponse,
            hybridResponseURL,
            pirateToken
        ));
    }
}
